import { BusinessContext } from '@ravago/shared/page-data/models/config';

import { z } from 'zod';

// Access Level enum values
const AccessLevelSchema = z.enum(['Public', 'Guest', 'Pending', 'Customer', 'Internal', 'Authenticated Only', 'Verified Only', 'CSR Only']);

// Image schema
const ImageSchema = z.looseObject({
  title: z.string().optional().nullable(),
  url: z.string()
});

// UOM schema
const UOMSchema = z.looseObject({
  id: z.enum(['imperial', 'metric']),
  value: z.string(),
  default: z.boolean()
});

// Page schema
const PageSchema = z.looseObject({
  route: z.string().optional().nullable(),
  slug: z.string().optional().nullable()
});

// NavigationItem schema
const NavigationItemSchema: z.ZodType<any> = z.lazy(() =>
  z.looseObject({
    icon: z.string().optional().nullable(),
    label: z.string(),
    description: z.string().optional().nullable(),
    page: PageSchema.optional().nullable(),
    slug: z.string().optional().nullable(),
    queryParameters: z.string().optional().nullable(),
    externalUrl: z.string().optional().nullable(),
    openInNewTab: z.boolean(),
    accessType: z.array(AccessLevelSchema),
    identifier: z.string().optional().nullable()
  })
);

// NavigationGroup schema
const NavigationGroupSchema: z.ZodType<any> = z.lazy(() =>
  z.looseObject({
    label: z.string(),
    icon: z.string().optional().nullable(),
    itemsCollection: z.looseObject({
      items: z.array(NavigationItemSchema)
    }),
    accessType: z.array(AccessLevelSchema)
  })
);

// NavigationDropdown schema
const NavigationDropdownSchema: z.ZodType<any> = z.lazy(() =>
  z.looseObject({
    sys: z.looseObject({
      id: z.string()
    }),
    label: z.string(),
    icon: z.string().optional().nullable(),
    itemsCollection: z.looseObject({
      items: z.array(z.union([NavigationItemSchema, NavigationGroupSchema]))
    }),
    accessType: z.array(AccessLevelSchema)
  })
);

// Header schema
const HeaderSchema = z.looseObject({
  internalName: z.string(),
  alignment: z.enum(['Left', 'Right']).optional().nullable(),
  navigationItemsCollection: z.looseObject({
    items: z.array(z.union([NavigationItemSchema, NavigationDropdownSchema]))
  })
});

// BusinessContext schema - matching the GraphQL model structure
export const BusinessContextSchema = z.looseObject({
  name: z.string(),
  logo: ImageSchema,
  favicon: ImageSchema,
  header: HeaderSchema.optional().nullable(),
  profileMenu: HeaderSchema.optional().nullable(),
  footerCollection: z
    .looseObject({
      items: z.array(NavigationGroupSchema)
    })
    .optional()
    .nullable(),
  footerDisabled: z.boolean().optional().nullable(),
  registrationLandingPage: NavigationItemSchema.optional().nullable(),
  emailAddress: z.string().nullable(),
  uomCollection: z
    .object({
      items: z.array(UOMSchema)
    })
    .optional()
    .nullable(),
  phoneNr: z.string().nullable(),
  linkedInUrl: z.string().nullable(),
  address: z.string().nullable(),
  twitterSite: z.string(),
  gtmId: z.string().nullable(),
  googleMapsApiKey: z.string().nullable(),
  countryLanguageTable: z
    .object({
      countryLanguagePairsCollection: z.looseObject({
        items: z.array(
          z.looseObject({
            country: z.looseObject({
              shortName: z.string()
            }),
            languagesCollection: z.looseObject({
              items: z.array(
                z.looseObject({
                  shortName: z.string()
                })
              )
            })
          })
        )
      })
    })
    .optional()
    .nullable(),
  excludedCountriesCollection: z
    .looseObject({
      items: z.array(
        z.looseObject({
          fullName: z.string(),
          shortName: z.string()
        })
      )
    })
    .optional()
    .nullable()
});

export function validateConfigurationEntry(data: BusinessContext) {
  return BusinessContextSchema.safeParse(data);
}
